/**
 * ARAM Platform - Auth Layout v1.2 (Close 2026 / 2027)
 * Hyperrealistic verticals video hero + fluorescent ethereal overlays.
 * Core Desarrollos S.A.S
 */

import { useState, useEffect } from 'react'
import { Outlet, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { AuthEffectCtx, prepareAuthSuccessHold, shouldHoldAuthRedirect } from '@/layouts/auth/AuthEffectCtx'

const VERTICALS = [
  { tag: 'ARAM Distribution', glow: 'from-fuchsia-400 to-cyan-300', blurb: 'Pedidos, dark stores y fulfillment en vivo' },
  { tag: 'Industria Inteligente', glow: 'from-violet-400 to-lime-300', blurb: 'MES, IoT y automatización de planta' },
  { tag: 'Forwarder', glow: 'from-cyan-300 to-pink-400', blurb: 'Freight, trade lanes y contenedores' },
  { tag: 'Agro', glow: 'from-lime-300 to-cyan-300', blurb: 'Campo, riego, drones y rendimiento' },
  { tag: 'Procesadoras & Manufactura', glow: 'from-pink-400 to-lime-300', blurb: 'OEE, calidad y trazabilidad de lote' },
  { tag: 'Minería & Energía', glow: 'from-fuchsia-400 to-violet-300', blurb: 'Haul trucks, sensores y tonelaje' },
  { tag: 'Rental & Assets', glow: 'from-cyan-300 to-fuchsia-400', blurb: 'Activos RFID, utilización y telemetría' },
  { tag: 'Trazabilidad RFID', glow: 'from-lime-300 to-fuchsia-400', blurb: 'Portales, inventario y chain of custody' },
  { tag: 'Centro de Control', glow: 'from-cyan-300 to-lime-300', blurb: 'Mapa vivo, alertas y near real-time' },
  { tag: 'Flota · Rutas', glow: 'from-violet-400 to-cyan-300', blurb: 'GPS, despacho y optimización' },
  { tag: 'People y RR. HH.', glow: 'from-pink-400 to-cyan-300', blurb: 'Personas, asistencia, nómina y comunidad' },
  { tag: 'IA · Reporting', glow: 'from-fuchsia-400 to-lime-300', blurb: 'Predicción, BI y datos en tiempo real' },
]

export function AuthLayoutV12() {
  const { isAuthenticated } = useAuthStore()
  const [featIdx, setFeatIdx] = useState(0)
  const [videoReady, setVideoReady] = useState(false)

  useEffect(() => {
    const t = setInterval(() => setFeatIdx((i) => (i + 1) % VERTICALS.length), 2800)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    const container = document.getElementById('aram-particles-v12')
    if (!container) return
    container.innerHTML = ''
    for (let i = 0; i < 42; i++) {
      const p = document.createElement('div')
      p.className = 'aram-particle-v12'
      p.style.left = `${Math.random() * 100}vw`
      p.style.animationDelay = `${Math.random() * 4}s`
      p.style.animationDuration = `${7 + Math.random() * 10}s`
      const size = 2 + Math.random() * 4
      p.style.width = `${size}px`
      p.style.height = `${size}px`
      const hues = ['#22d3ee', '#e879f9', '#a3e635', '#c084fc', '#f472b6']
      p.style.background = hues[i % hues.length]
      p.style.boxShadow = `0 0 10px ${hues[i % hues.length]}`
      container.appendChild(p)
    }
    return () => {
      container.innerHTML = ''
      document.body.classList.remove('auth-error', 'auth-success')
    }
  }, [])

  if (isAuthenticated && !shouldHoldAuthRedirect()) return <Navigate to="/" replace />

  const prepareSuccess = () => { prepareAuthSuccessHold(5000) }

  const feat = VERTICALS[featIdx]

  return (
    <AuthEffectCtx.Provider value={{ prepareSuccess }}>
      <>
        <style>{`
          @keyframes particleRiseV12{
            0%{transform:translateY(105vh) translateX(0) scale(0);opacity:0}
            12%{opacity:1;transform:translateY(85vh) translateX(20px) scale(1)}
            100%{transform:translateY(-8vh) translateX(-24px) scale(.4);opacity:0}
          }
          .aram-particle-v12{
            position:fixed;border-radius:50%;pointer-events:none;z-index:2;
            animation:particleRiseV12 14s linear infinite;
          }
          @keyframes auroraDrift{
            0%,100%{transform:translate3d(0,0,0) scale(1);opacity:.55}
            50%{transform:translate3d(4%,-3%,0) scale(1.08);opacity:.85}
          }
          .aurora{animation:auroraDrift 14s ease-in-out infinite}
          .aurora2{animation:auroraDrift 18s ease-in-out infinite reverse}
          @keyframes featIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
          .feat-v12{animation:featIn .4s ease forwards}
          @keyframes neonPulse{
            0%,100%{box-shadow:0 0 0 1px rgba(34,211,238,.25),0 0 40px rgba(232,121,249,.15)}
            50%{box-shadow:0 0 0 1px rgba(232,121,249,.45),0 0 60px rgba(34,211,238,.28)}
          }
          .auth-card-v12{animation:neonPulse 4.5s ease-in-out infinite}
          body.auth-success .auth-card-v12{
            box-shadow:0 0 0 2px rgba(52,211,153,.9),0 0 80px rgba(16,185,129,.55)!important;
          }
          body.auth-error .auth-card-v12{
            box-shadow:0 0 0 2px rgba(248,113,113,.9),0 0 80px rgba(239,68,68,.45)!important;
          }
          @keyframes successFlash{
            0%{opacity:0}10%{opacity:1}100%{opacity:0}
          }
          body.auth-success::before{
            content:'';position:fixed;inset:0;z-index:9998;pointer-events:none;
            background:radial-gradient(ellipse at 60% 50%,rgba(16,185,129,.75),transparent 70%);
            animation:successFlash 2.6s ease-out forwards;
          }
          body.auth-error::before{
            content:'';position:fixed;inset:0;z-index:9998;pointer-events:none;
            background:radial-gradient(ellipse at 60% 50%,rgba(239,68,68,.55),transparent 70%);
            animation:successFlash 2.8s ease-out forwards;
          }
        `}</style>

        <div className="min-h-screen bg-black flex overflow-hidden relative">
          {/* Video background */}
          <div className="pointer-events-none absolute inset-0 z-0">
            <video
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
              autoPlay
              muted
              loop
              playsInline
              poster="/auth/aram-login-v12-poster.jpg"
              onCanPlay={() => setVideoReady(true)}
            >
              <source src="/auth/aram-login-v12-hero.mp4" type="video/mp4" />
            </video>
            {!videoReady && (
              <img
                src="/auth/aram-login-v12-poster.jpg"
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
            {/* Dark + fluorescent wash for readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/35" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_30%,rgba(232,121,249,0.22),transparent_50%),radial-gradient(ellipse_at_80%_70%,rgba(34,211,238,0.2),transparent_45%),radial-gradient(ellipse_at_50%_100%,rgba(163,230,53,0.12),transparent_40%)]" />
            <div className="aurora absolute -top-1/4 -left-1/4 h-[70%] w-[70%] rounded-full bg-fuchsia-500/20 blur-[120px]" />
            <div className="aurora2 absolute -bottom-1/4 -right-1/4 h-[65%] w-[65%] rounded-full bg-cyan-400/20 blur-[130px]" />
          </div>

          <div id="aram-particles-v12" className="pointer-events-none fixed inset-0 z-[2] overflow-hidden" />

          {/* LEFT */}
          <div className="hidden lg:flex flex-col w-[56%] relative z-10 p-14 justify-between">
            <div className="flex items-center gap-3.5">
              {/* Brand mark — original ARAM colors (blue → cyan → emerald); do not recolor */}
              <svg viewBox="0 0 64 44" className="h-10 w-auto flex-shrink-0 drop-shadow-[0_0_20px_rgba(59,130,246,.7)]" fill="none" aria-label="ARAM">
                <defs>
                  <linearGradient id="v12a" x1="15" y1="22" x2="40" y2="22" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                  <linearGradient id="v12b" x1="44" y1="22" x2="60" y2="22" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
                <circle cx="10" cy="22" r="5" fill="#3b82f6" />
                <path d="M15 22 Q28 10 40 22" stroke="url(#v12a)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <circle cx="40" cy="22" r="4" fill="url(#v12a)" />
                <path d="M44 22 Q52 32 60 22" stroke="url(#v12b)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <circle cx="60" cy="22" r="5" fill="#10b981" />
              </svg>
              <div>
                <div className="text-white text-[26px] font-black tracking-tight leading-none">ARAM</div>
                <div className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent text-[10px] font-bold tracking-[0.22em] uppercase mt-0.5">
                  Platform · v1.2
                </div>
              </div>
            </div>

            <div className="space-y-9 max-w-xl">
              <div>
                <p className="text-cyan-300 text-xs font-bold uppercase tracking-[0.22em] mb-4 drop-shadow-[0_0_12px_rgba(34,211,238,.6)]">
                  Operación multi‑vertical · near real‑time
                </p>
                <h1 className="text-[2.85rem] font-extrabold text-white leading-[1.08] tracking-tight">
                  Distribution, industria, agro,
                  <br />
                  minería, flota y people —
                  <span className="block mt-1 bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-lime-300 bg-clip-text text-transparent">
                    todas las verticales, vivas.
                  </span>
                </h1>
                <p className="mt-5 text-slate-300/90 text-base leading-relaxed">
                  Forwarder, RFID, rental, manufactura, centro de control, rutas, IA y reporting
                  con datos en tiempo real o near real‑time — el cierre visual 2026–2027.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/35 backdrop-blur-xl p-5 shadow-[0_0_40px_rgba(232,121,249,0.12)]">
                <div key={featIdx} className="feat-v12">
                  <div className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black bg-gradient-to-r ${feat.glow}`}>
                    {feat.tag}
                  </div>
                  <p className="mt-3 text-white font-semibold text-[15px]">{feat.blurb}</p>
                </div>
                <div className="flex gap-1.5 mt-4">
                  {VERTICALS.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 rounded-full transition-all duration-500 ${
                        i === featIdx ? 'w-7 bg-gradient-to-r from-cyan-300 to-fuchsia-400' : 'w-1.5 bg-white/20'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span>© {new Date().getFullYear()} Core Desarrollos S.A.S · Mendoza, Argentina</span>
              <a href="https://aram.com.ar" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300 transition-colors">
                aram.com.ar ↗
              </a>
            </div>
          </div>

          {/* RIGHT */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6 lg:p-14">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <svg viewBox="0 0 64 44" className="h-8 w-auto flex-shrink-0" fill="none" aria-label="ARAM">
                <defs>
                  <linearGradient id="v12m1" x1="15" y1="22" x2="40" y2="22" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                  <linearGradient id="v12m2" x1="44" y1="22" x2="60" y2="22" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
                <circle cx="10" cy="22" r="5" fill="#3b82f6" />
                <path d="M15 22 Q28 10 40 22" stroke="url(#v12m1)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <circle cx="40" cy="22" r="4" fill="url(#v12m1)" />
                <path d="M44 22 Q52 32 60 22" stroke="url(#v12m2)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <circle cx="60" cy="22" r="5" fill="#10b981" />
              </svg>
              <span className="text-white text-xl font-black tracking-tight">ARAM</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400">v1.2</span>
            </div>

            <div
              className="auth-card-v12 w-full max-w-[420px] rounded-2xl p-8 lg:p-10 bg-white/95 backdrop-blur-xl border border-white/40"
              style={{ boxShadow: '0 32px 80px rgba(0,0,0,.55)' }}
            >
              <Outlet />
            </div>

            <div className="mt-5 flex items-center gap-2 text-slate-400 text-[11px]">
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Conexión encriptada · OWASP Compliant · JWT Auth
            </div>
          </div>
        </div>
      </>
    </AuthEffectCtx.Provider>
  )
}
